import React from "react";

function collapseWhitespace(text: string) {
  return text.replace(/\s+/g, " ").trim();
}

/** Avoid showing title + body when composer stored the first line as both. */
export function getPostDisplayContent(post: {
  title?: string;
  body?: string;
}): { headline: string | null; body: string } {
  const title = (post.title || "").trim();
  const body = (post.body || "").trim();

  if (!body && title) {
    return { headline: null, body: title };
  }
  if (!title) {
    return { headline: null, body };
  }

  const titleNorm = collapseWhitespace(title);
  const bodyNorm = collapseWhitespace(body);

  if (titleNorm === bodyNorm) {
    return { headline: null, body };
  }

  const firstLine = body.split(/\n/)[0]?.trim() || "";
  const firstLineNorm = collapseWhitespace(firstLine);

  if (
    titleNorm === firstLineNorm ||
    bodyNorm.startsWith(titleNorm) ||
    firstLineNorm.startsWith(titleNorm)
  ) {
    return { headline: null, body };
  }

  return { headline: title, body };
}

export default function CommunityPostContent({
  title,
  body,
  className = "",
}: {
  title?: string;
  body?: string;
  className?: string;
}) {
  const { headline, body: displayBody } = getPostDisplayContent({ title, body });

  if (!displayBody && !headline) {
    return null;
  }

  if (headline) {
    return (
      <div className={className}>
        <h2 className="text-lg font-semibold text-[#15415E] leading-snug mb-2">
          {headline}
        </h2>
        {displayBody ? (
          <p className="text-[15px] text-[#4B5563] leading-relaxed whitespace-pre-line">
            {displayBody}
          </p>
        ) : null}
      </div>
    );
  }

  return (
    <p
      className={`text-[15px] text-[#374151] leading-relaxed whitespace-pre-line ${className}`.trim()}
    >
      {displayBody}
    </p>
  );
}

/** Single string for editing a post (matches what the user sees in the feed). */
export function getPostEditableText(post: {
  title?: string;
  body?: string;
}) {
  const { headline, body } = getPostDisplayContent(post);
  if (headline && body) {
    return `${headline}\n\n${body}`;
  }
  return body || headline || "";
}

export function formatCommentCount(count: number) {
  return `${count} comment${count === 1 ? "" : "s"}`;
}
